export const POLYGON_STYLE = {
  strokeColor: "#b91c1c",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: "#b91c1c",
  fillOpacity: 0.35,
  clickable: false
}

const SIMPLIFY_TOLERANCE_PX = 4
const COORDINATE_PRECISION = 6

export class PolygonDrawing {
  constructor(map, { polygonOptions = {}, onFinish }) {
    this.map = map
    this.polygonOptions = { ...POLYGON_STYLE, ...polygonOptions }
    this.onFinish = onFinish
    this.projectionOverlay = this.createProjectionOverlay()
  }

  start() {
    this.surface = document.createElement("div")
    this.surface.className = "absolute inset-0 z-10 cursor-crosshair touch-none"
    this.surface.addEventListener("pointerdown", this.beginStroke)
    this.surface.addEventListener("pointermove", this.extendStroke)
    this.surface.addEventListener("pointerup", this.endStroke)
    this.surface.addEventListener("pointercancel", this.endStroke)
    this.map.getDiv().append(this.surface)
  }

  cancel() {
    if (this.surface) {
      this.stop()
      this.onFinish(null)
    }
  }

  beginStroke = (event) => {
    if (event.isPrimary && !this.stroke) {
      this.surface.setPointerCapture(event.pointerId)
      this.points = [this.containerPoint(event)]
      this.stroke = new google.maps.Polyline({
        ...POLYGON_STYLE,
        path: [this.toLatLng(this.points[0])],
        map: this.map
      })
    }
  }

  extendStroke = (event) => {
    if (event.isPrimary && this.stroke) {
      const point = this.containerPoint(event)
      this.points.push(point)
      this.stroke.getPath().push(this.toLatLng(point))
    }
  }

  endStroke = (event) => {
    if (event.isPrimary && this.stroke) {
      const corners = simplify(this.points, SIMPLIFY_TOLERANCE_PX)
      this.stop()

      if (corners.length < 3) {
        this.onFinish(null)
      } else {
        this.onFinish(new google.maps.Polygon({
          ...this.polygonOptions,
          paths: corners.map((point) => this.toLatLng(point)),
          map: this.map
        }))
      }
    }
  }

  stop() {
    this.stroke?.setMap(null)
    this.stroke = null
    this.points = []
    this.surface.remove()
    this.surface = null
  }

  containerPoint(event) {
    const { left, top } = this.map.getDiv().getBoundingClientRect()
    return { x: event.clientX - left, y: event.clientY - top }
  }

  toLatLng({ x, y }) {
    const latLng = this.projectionOverlay.getProjection().fromContainerPixelToLatLng(new google.maps.Point(x, y))
    return new google.maps.LatLng(round(latLng.lat()), round(latLng.lng()))
  }

  createProjectionOverlay() {
    const overlay = new google.maps.OverlayView()
    overlay.onAdd = () => {}
    overlay.draw = () => {}
    overlay.onRemove = () => {}
    overlay.setMap(this.map)
    return overlay
  }
}

function round(degrees) {
  return Number(degrees.toFixed(COORDINATE_PRECISION))
}

// Douglas–Peucker: keep the points that stray more than `tolerance` from the line between their neighbours
function simplify(points, tolerance) {
  if (points.length < 3) {
    return points
  }

  const first = points[0]
  const last = points[points.length - 1]
  let farthestIndex = 0
  let farthestDistance = 0

  for (let index = 1; index < points.length - 1; index++) {
    const distance = distanceToSegment(points[index], first, last)
    if (distance > farthestDistance) {
      farthestIndex = index
      farthestDistance = distance
    }
  }

  if (farthestDistance > tolerance) {
    return [
      ...simplify(points.slice(0, farthestIndex + 1), tolerance).slice(0, -1),
      ...simplify(points.slice(farthestIndex), tolerance)
    ]
  } else {
    return [first, last]
  }
}

function distanceToSegment(point, start, end) {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const lengthSquared = dx * dx + dy * dy
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared))
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy))
}
